import TemplateContents from './TemplateContents';
import { gsap } from 'gsap';
import { Draggable } from 'gsap/Draggable';

export default class extends TemplateContents {
  constructor(param) {
    super(param);
  }

  init() {
    gsap.registerPlugin(Draggable);
    super.init();
    if (this.items.length > 0) {
      this.setupCarousel();
    }
  }

  destruct() {
    super.destruct();
    if (this.nextBtn) this.nextBtn.removeEventListener('click', this.handleNext);
    if (this.prevBtn) this.prevBtn.removeEventListener('click', this.handlePrev);
    if (this.dots && this.dotClickHandlers) {
      this.dots.forEach((dot, index) => {
        dot.removeEventListener('click', this.dotClickHandlers[index]);
      });
    }
    if (this.draggable) {
      this.draggable[0].kill();
    }
    // このインスタンスに関連するすべてのGSAPトゥイーンを停止
    gsap.killTweensOf(this.carousel);
  }

  setVars() {
    super.setVars();
    this.carouselWrap = null;
    this.carousel = null;
    this.items = null;
    this.allItems = null;
    this.nextBtn = null;
    this.prevBtn = null;
    this.dots = null;
    this.itemWidth = 0;
    this.cloneCount = 2; // ループをスムーズにするため、両端に複製するアイテムの数
    this.totalItems = 0;
    this.currentIndex = this.cloneCount;
    this.isAnimating = false;
    this.handleNext = null;
    this.handlePrev = null;
    this.dotClickHandlers = [];
    this.draggable = null;
  }

  setDom() {
    super.setDom();
    this.carouselWrap = document.querySelector('.section2 .carousel_wrap');
    this.carousel = document.querySelector('.section2 .carousel');
    this.items = this.carousel.querySelectorAll('.carousel_item');
    this.nextBtn = document.querySelector('.section2 .next_bt');
    this.prevBtn = document.querySelector('.section2 .prev_bt');
    this.dots = document.querySelectorAll('.section2 .carousel_ui ul li');
  }

  initEvents() {
    super.initEvents();
    this.handleNext = () => {
      if (this.isAnimating) return;
      this.goToSlide(this.currentIndex + 1);
    };
    this.handlePrev = () => {
      if (this.isAnimating) return;
      this.goToSlide(this.currentIndex - 1);
    };

    this.nextBtn.addEventListener('click', this.handleNext);
    this.prevBtn.addEventListener('click', this.handlePrev);

    this.dots.forEach((dot, index) => {
      const handler = () => {
        if (this.isAnimating) return;
        this.goToSlide(index + this.cloneCount); // 先頭のクローン数を考慮してインデックスを調整
      };
      this.dotClickHandlers.push(handler);
      dot.addEventListener('click', handler);
    });
  }

  setupCarousel() {
    this.carousel.style.cursor = 'grab';
    this.totalItems = this.items.length;

    // アイテム数が少ない場合、クローン数を調整
    if (this.totalItems > 1 && this.totalItems <= this.cloneCount) {
      this.cloneCount = this.totalItems - 1;
    }
    this.currentIndex = this.cloneCount; // 初期位置を最初のオリジナルアイテムに設定

    // アイテムをクローンしてループを作成
    for (let i = 0; i < this.cloneCount; i++) {
      // 末尾のアイテムを複製して先頭に挿入
      const lastClone = this.items[this.totalItems - 1 - i].cloneNode(true);
      this.carousel.insertBefore(lastClone, this.carousel.firstChild);
      // 先頭のアイテムを複製して末尾に追加
      const firstClone = this.items[i].cloneNode(true);
      this.carousel.appendChild(firstClone);
    }

    this.allItems = this.carousel.querySelectorAll('.carousel_item');
    
    this.executeResize(); // 初期位置設定
    this.updateDots();
    this.initDraggable();
  }

  goToSlide(index) {
    if (this.isAnimating) return;
    this.isAnimating = true;
    this.currentIndex = index;
    this.updatePosition();
    this.updateDots();
  }

  updatePosition(animate = true) {
    const containerWidth = this.carouselWrap.offsetWidth;
    const itemMargin = parseFloat(getComputedStyle(this.allItems[0]).marginRight) || 0;
    const itemOffsetWidth = this.allItems[0].offsetWidth;
    this.itemWidth = itemOffsetWidth + itemMargin;
    
    // 中央揃えの計算ではマージンを含まない`itemOffsetWidth`を使用し、全体のオフセット計算ではマージンを含む`this.itemWidth`を使用します。
    // これにより、マージンによるずれを解消します。
    const newX = (containerWidth - itemOffsetWidth) / 2 - (this.currentIndex * this.itemWidth);

    if (animate) {
      gsap.to(this.carousel, {
        x: newX,
        duration: 0.7,
        ease: 'quart.inOut',
        onComplete: () => {
          this.checkLoop();
          this.isAnimating = false;
        }
      });
    } else {
      gsap.set(this.carousel, { x: newX });
    }
  }

  checkLoop() {
    const firstOriginalIndex = this.cloneCount;
    const lastOriginalIndex = this.cloneCount + this.totalItems - 1;
    let jumped = false; // Flag to indicate if a position jump occurred

    // 最後のクローン(最初のアイテムのクローン)に到達したら
    if (this.currentIndex > lastOriginalIndex) {
      this.currentIndex -= this.totalItems; // 対応するオリジナルアイテムの位置にジャンプ
      this.updatePosition(false);
      jumped = true;
    }
    // 最初のクローン(最後のアイテムのクローン)に到達したら
    if (this.currentIndex < firstOriginalIndex) {
      this.currentIndex += this.totalItems; // 対応するオリジナルアイテムの位置にジャンプ
      this.updatePosition(false);
      jumped = true;
    }
    return jumped;
  }

  updateDots() {
    this.dots.forEach(dot => dot.classList.remove('current'));
    // currentIndexはクローンを含んだDOMインデックスなので、実際の0-basedのdotインデックスに変換
    const actualIndex = (this.currentIndex - this.cloneCount + this.totalItems) % this.totalItems;
    if (this.dots[actualIndex]) {
      this.dots[actualIndex].classList.add('current');
    }
  }

  executeResize() {
    super.executeResize();
    if (this.allItems) {
      // ループ状態をチェックし、必要であればインデックスと位置を補正します。
      // これにより、リサイズ後の最初の操作でガタつくのを防ぎます。
      const jumped = this.checkLoop();

      // checkLoopで位置が更新されなかった場合、リサイズに合わせて位置を再計算します。
      if (!jumped) {
        this.updatePosition(false);
      }

      // Draggableの内部状態を更新して、新しい位置やサイズに同期させます。
      if (this.draggable) {
        this.draggable[0].update(true);
      }
    }
  }

  initDraggable() {
    this.draggable = Draggable.create(this.carousel, {
      type: 'x',
      edgeResistance: 0.8,
      onDragStart: () => {
        // アニメーションの途中でドラッグを開始した場合でも、ループ状態を即座に補正する
        const jumped = this.checkLoop();
        if (jumped) {
          // Draggableの位置情報を更新して、見た目の位置と同期させる
          this.draggable[0].update(true);
        }
        this.isAnimating = false;
        this.carousel.style.cursor = 'grabbing';
        gsap.killTweensOf(this.carousel);
      },
      onDragEnd: () => {
        this.carousel.style.cursor = 'grab';
        if (this.isAnimating) return;

        const velocityX = this.draggable[0].getDirection('x') === 'left' ? -this.draggable[0].velocityX : this.draggable[0].velocityX;
        const dragDistance = this.draggable[0].x - this.draggable[0].startX;

        const velocityThreshold = 200; // pixels per second
        const distanceThreshold = this.itemWidth / 8;

        if (Math.abs(velocityX) > velocityThreshold || Math.abs(dragDistance) > distanceThreshold) {
          this.goToSlide(this.currentIndex + (dragDistance < 0 ? 1 : -1));
        } else {
          // Snap back to current position
          this.updatePosition();
        }
      },
    });
  }
}